import React from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    Avatar,
    Tooltip
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import VisibilityIcon from '@material-ui/icons/Visibility'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'
import StarIcon from '@material-ui/icons/Star'
import StarHalfIcon from '@material-ui/icons/StarHalf'
import styled from 'styled-components'
import { DB } from '../../firebase'

function TestimonialTable({ testimonialData, tools, onEdit }) {
    
    // Activate testimonial
    const handleActivate = async (id) => {
        if (window.confirm('Are you sure you want to activate this testimonial?')) {
            try {
                await DB.collection('testimonials').doc(id).update({
                    active: true
                })
                alert('Testimonial activated successfully!')
            } catch (error) {
                console.error('Error activating testimonial:', error)
                alert('Error activating testimonial')
            }
        }
    }

    // Deactivate testimonial
    const handleDeactivate = async (id) => {
        if (window.confirm('Are you sure you want to deactivate this testimonial?')) {
            try {
                await DB.collection('testimonials').doc(id).update({
                    active: false
                })
                alert('Testimonial deactivated successfully!')
            } catch (error) {
                console.error('Error deactivating testimonial:', error)
                alert('Error deactivating testimonial')
            }
        }
    }

    // Delete testimonial
    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this testimonial? This action cannot be undone.')) {
            try {
                await DB.collection('testimonials').doc(id).delete()
                alert('Testimonial deleted successfully!')
            } catch (error) {
                console.error('Error deleting testimonial:', error)
                alert('Error deleting testimonial')
            }
        }
    }

    // Render star rating
    const renderStars = (rating) => {
        const stars = []
        const fullStars = Math.floor(rating)
        const hasHalfStar = rating % 1 !== 0

        for (let i = 0; i < fullStars; i++) {
            stars.push(
                <StarIcon 
                    key={`full-${i}`} 
                    style={{ color: '#f39c12', fontSize: '1.2rem' }} 
                />
            )
        }

        if (hasHalfStar) {
            stars.push(
                <StarHalfIcon 
                    key="half" 
                    style={{ color: '#f39c12', fontSize: '1.2rem' }} 
                />
            )
        }

        return <StarContainer>{stars}</StarContainer>
    }

    // Format timestamp
    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A'
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        })
    }

    return (
        <StyledTableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <StyledTableCell>Image</StyledTableCell>
                        <StyledTableCell>Name</StyledTableCell>
                        <StyledTableCell>Testimonial</StyledTableCell>
                        <StyledTableCell align="center">Rating</StyledTableCell>
                        <StyledTableCell>Role</StyledTableCell>
                        <StyledTableCell align="center">Class</StyledTableCell>
                        <StyledTableCell>Subject</StyledTableCell>
                        <StyledTableCell align="center">Date Added</StyledTableCell>
                        <StyledTableCell align="center">Actions</StyledTableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {testimonialData.map((testimonial) => (
                        <StyledTableRow key={testimonial.id}>
                            <TableCell>
                                <Avatar
                                    src={testimonial.image}
                                    alt={testimonial.name}
                                    style={{ width: 60, height: 60, borderRadius: '10px' }}
                                />
                            </TableCell>
                            <TableCell>
                                <strong>{testimonial.name}</strong>
                            </TableCell>
                            <TableCell>
                                <TestimonialText>
                                    {testimonial.testimonial?.length > 100
                                        ? `${testimonial.testimonial.substring(0, 100)}...`
                                        : testimonial.testimonial}
                                </TestimonialText>
                            </TableCell>
                            <TableCell align="center">
                                {renderStars(testimonial.rating)}
                                <RatingNumber>{testimonial.rating}</RatingNumber>
                            </TableCell>
                            <TableCell>
                                <Chip
                                    label={testimonial.designation}
                                    size="small"
                                    style={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        fontWeight: 500
                                    }}
                                />
                            </TableCell>
                            <TableCell align="center">
                                {testimonial.studentClass || '-'}
                            </TableCell>
                            <TableCell>
                                {testimonial.subject || '-'}
                            </TableCell>
                            <TableCell align="center">
                                <DateText>{formatDate(testimonial.timestamp)}</DateText>
                            </TableCell>
                            <TableCell align="center">
                                <ActionsContainer>
                                    {tools.includes('edit') && (
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                onClick={() => onEdit(testimonial)}
                                                style={{ color: '#3498db' }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {tools.includes('activate') && (
                                        <Tooltip title="Activate">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleActivate(testimonial.id)}
                                                style={{ color: '#27ae60' }}
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {tools.includes('deactivate') && (
                                        <Tooltip title="Deactivate">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeactivate(testimonial.id)}
                                                style={{ color: '#e67e22' }}
                                            >
                                                <VisibilityOffIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {tools.includes('delete') && (
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDelete(testimonial.id)}
                                                style={{ color: '#e74c3c' }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </ActionsContainer>
                            </TableCell>
                        </StyledTableRow>
                    ))}
                </TableBody>
            </Table>
        </StyledTableContainer>
    )
}

const StyledTableContainer = styled(TableContainer)`
    box-shadow: 0px 8px 12px rgba(0, 0, 0, 0.07);
    border-radius: 15px;
    overflow: hidden;
`

const StyledTableCell = styled(TableCell)`
    && {
        font-weight: 700;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        font-size: 0.95rem;
    }
`

const StyledTableRow = styled(TableRow)`
    &:nth-of-type(odd) {
        background-color: #f8f9fa;
    }
    &:hover {
        background-color: #e8eaf6;
        transition: background-color 0.3s ease;
    }
`

const TestimonialText = styled.div`
    max-width: 300px;
    color: #555;
    font-size: 0.9rem;
    line-height: 1.5;
`

const StarContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 2px;
    margin-bottom: 5px;
`

const RatingNumber = styled.div`
    font-size: 0.85rem;
    color: #666;
    font-weight: 600;
`

const DateText = styled.div`
    font-size: 0.85rem;
    color: #666;
`

const ActionsContainer = styled.div`
    display: flex;
    gap: 5px;
    justify-content: center;
`

export default TestimonialTable